import React from 'react';
import { Box, CircularProgress, TextField, Typography } from '@mui/material';

import AppPagination from '@/__core/components/AppPagination';
import DataTableModule, { TableColumn } from '@/__core/layout/module/DataTableModule';
import { sharedListStyles } from '@/__core/styles/sharedListStyles';

import { RelatedEntityTabRecord } from './openOrFocusEntityTab';

export interface RelatedEntityPagination {
  count: number;
  page: number;
  pageSize: number;
  totalPages: number;
  itemLabel: string;
  onPageChange: (page: number) => void;
}

interface RelatedEntityListTabProps<T> {
  rows: T[];
  columns: TableColumn[];
  loading?: boolean;
  searchPlaceholder: string;
  matchesSearch: (row: T, normalizedQuery: string) => boolean;
  emptyTitle: string;
  emptyDescription?: string;
  getRowId: (row: T) => string;
  buildTabRecord?: (row: T) => RelatedEntityTabRecord<T>;
  onOpenRecord?: (record: RelatedEntityTabRecord<T>) => void;
  pagination?: RelatedEntityPagination;
  resetKey?: string;
}

/** Reusable relation-tab list: search, states, table, pagination and entity-tab handoff. */
export default function RelatedEntityListTab<T>({
  rows,
  columns,
  loading = false,
  searchPlaceholder,
  matchesSearch,
  emptyTitle,
  emptyDescription,
  getRowId,
  buildTabRecord,
  onOpenRecord,
  pagination,
  resetKey,
}: RelatedEntityListTabProps<T>) {
  const [search, setSearch] = React.useState('');

  React.useEffect(() => setSearch(''), [resetKey]);

  const filteredRows = React.useMemo(() => {
    const query = search.trim().toLocaleLowerCase('vi-VN');
    return query ? rows.filter((row) => matchesSearch(row, query)) : rows;
  }, [matchesSearch, rows, search]);

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', minWidth: 0, overflowX: 'hidden' }}>
      <TextField
        size='small'
        fullWidth
        placeholder={searchPlaceholder}
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        sx={{ mb: 2 }}
      />

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
          <CircularProgress size={28} />
        </Box>
      ) : filteredRows.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column', gap: 1, height: 220 }}>
          <Typography variant='body1' color='text.secondary'>
            {search ? 'Không tìm thấy kết quả' : emptyTitle}
          </Typography>
          {!search && emptyDescription ? (
            <Typography variant='body2' color='text.secondary'>{emptyDescription}</Typography>
          ) : null}
        </Box>
      ) : (
        <Box sx={{ ...sharedListStyles.container, width: '100%', maxWidth: '100%', minWidth: 0, overflowX: 'auto' }}>
          <DataTableModule
            data={filteredRows}
            columns={columns}
            leftFilterWidth={0}
            onRowSelect={() => undefined}
            onSelectAll={() => undefined}
            getRowId={(row) => getRowId(row as T)}
            isRowSelected={() => false}
            areAllRowsSelected={false}
            areSomeRowsSelected={false}
            showCheckbox={false}
            onRowClick={onOpenRecord && buildTabRecord
              ? (row) => onOpenRecord(buildTabRecord(row as T))
              : undefined}
          />
        </Box>
      )}

      {pagination ? (
        <AppPagination
          count={pagination.count}
          page={pagination.page}
          pageSize={pagination.pageSize}
          totalPages={pagination.totalPages}
          itemLabel={pagination.itemLabel}
          onPageChange={(_event, page) => pagination.onPageChange(page)}
        />
      ) : null}
    </Box>
  );
}
